import React, { memo, useEffect, useCallback, useState, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { optionalPhoneSchema, optionalMobileSchema } from '@/utils/phoneValidation';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Heart, Info, Loader2 } from 'lucide-react';
import { User } from '@/types/User';
import { useEditUserRoles } from '@/hooks/useEditUserRoles';
import { useEditUserForm, EditUserFormData } from '@/hooks/useEditUserForm';
import PageLoader from '@/components/common/PageLoader';
import FormSection from '@/components/user-form/FormSection';
import InterestsInput from '@/components/user-form/InterestsInput';
import EditUserAvatarSection from '@/components/edit-user/EditUserAvatarSection';
import { toast } from 'sonner';
import axios from 'axios';

// Retry dynamic imports once on failure (handles stale chunk hashes after redeployment)
function lazyWithRetry(importFn: () => Promise<any>) {
  return React.lazy(() =>
    importFn().catch(() => {
      // Chunk likely stale after deploy - reload to get fresh bundle
      window.location.reload();
      return importFn();
    })
  );
}

// Lazy load form sections for better performance
const EditUserDetailsSection = lazyWithRetry(() => import('@/components/edit-user/EditUserDetailsSection'));
const EditUserRolesSection = lazyWithRetry(() => import('@/components/edit-user/EditUserRolesSection'));
const EditUserAdditionalInfoSection = lazyWithRetry(() => import('@/components/edit-user/EditUserAdditionalInfoSection'));

const editUserSchema = z.object({
  email: z.string().email(),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  middle_name: z.string().optional(),
  username: z.string().min(1, 'Username is required'),
  timezone: z.string().min(1, 'Timezone is required'),
  roles: z.array(z.string()).min(1, 'At least one role is required'),
  avatar: z.string().optional(),
  gender: z.string().optional(),
  birthdate: z.string().optional(),
  interests: z.string().optional(),
  location: z.string().optional(),
  job_role: z.string().optional(),
  phone: optionalPhoneSchema,
  mobile_number: optionalMobileSchema,
  department: z.string().optional(),
  employee_id: z.string().optional(),
  hire_date: z.string().optional(),
  bio: z.string().optional(),
});

interface EditUserFormProps {
  user: User;
  onUserUpdated: () => void;
  onDataRefresh?: () => void;
  onCancel: () => void;
  isCurrentUser: boolean;
}

export const EditUserForm = memo<EditUserFormProps>(({ user, onUserUpdated, onDataRefresh, onCancel, isCurrentUser }) => {

  const { user: currentUser } = useAuth();
  const { availableRoles, loadingRoles, fetchRoles } = useEditUserRoles();
  const { loading: hookLoading, submitForm, getInitials } = useEditUserForm();
  const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [avatarRemoved, setAvatarRemoved] = useState(false);
  const [loading, setLoading] = useState(false);
  const form = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      middle_name: user.middle_name || '',
      username: user.username || '',
      timezone: user.timezone || 'UTC',
      roles: user.roles || [],
      avatar: user.avatar || '',
      gender: user.gender || '',
      birthdate: user.birthdate || '',
      interests: user.interests || "",
      location: user.location || '',
      job_role: user.job_role || '',
      phone: user.phone || '',
      mobile_number: user.mobile_number || '',
      department: user.department || '',
      employee_id: user.employee_id || '',
      hire_date: user.hire_date || '',
      bio: user.bio || '',
    },
  });

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Reset form when user prop changes (e.g., after data refresh)
  useEffect(() => {
    const formData = {
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      middle_name: user.middle_name || '',
      username: user.username || '',
      timezone: user.timezone || 'UTC',
      roles: user.roles || [],
      avatar: user.avatar || '',
      gender: user.gender || '',
      birthdate: user.birthdate || '',
      interests: user.interests || "",
      location: user.location || '',
      job_role: user.job_role || '',
      phone: user.phone || '',
      mobile_number: user.mobile_number || '',
      department: user.department || '',
      employee_id: user.employee_id || '',
      hire_date: user.hire_date || '',
      bio: user.bio || '',
    };
    form.reset(formData);

    // Clear avatar preview when user data changes
    setAvatarPreviewUrl(null);
    setSelectedAvatarFile(null);
    setAvatarRemoved(false);
  }, [user, form]);

  const handleAvatarFileSelection = useCallback((file: File | null) => {
    setSelectedAvatarFile(file);
    setAvatarRemoved(file === null);

    if (file) {
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;
        setAvatarPreviewUrl(preview);
      };
      reader.readAsDataURL(file);
    } else {
      setAvatarPreviewUrl(null);
    }
  }, []);

  const onSubmit = useCallback(async (data: EditUserFormData) => {
    setLoading(true);
    try {
      await submitForm(
        user,
        data,
        onUserUpdated,
        onCancel,
        selectedAvatarFile,
        isCurrentUser
      );
    } finally {
      setLoading(false);
    }
  }, [submitForm, user, currentUser?.id, onUserUpdated, onCancel, selectedAvatarFile, isCurrentUser]);

  const handleSave = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) e.preventDefault();
      if (loading) return;

      // Check for date picker errors before saving
      const datePickerWithError = document.querySelector('[data-has-error="true"]');
      if (datePickerWithError) {
        toast.error('Please fix the invalid birth date before saving.');
        return;
      }

      const isValid = await form.trigger();
      if (isValid) {
        setLoading(true);
        try {
          const data = form.getValues();

          let finalData = { ...data };
          let avatarPayload: { user_image: string } | Record<string, never> = {};
          if (selectedAvatarFile) {
            const { fileUpload } = await import('@/utils/ERPUtils');
            const result = await fileUpload(selectedAvatarFile, 5, { folderToBeUploaded: 'user_profile_images' });
            if (result.error || !result.url) {
              toast.error(result.error || 'Profile image upload failed');
              return;
            }
            finalData.avatar = result.url;
            avatarPayload = { user_image: result.url };
            toast.success('Profile image uploaded successfully');
          } else if (avatarRemoved || !finalData.avatar) {
            // User hit Remove (explicit flag) or the field is empty -> clear the
            // stored image. Sending '' when there was nothing anyway is a no-op.
            avatarPayload = { user_image: '' };
          }
          // else: unchanged -> leave user_image out of the payload entirely.

          // Update user profile
          const { getApiEndpoint } = await import('@/utils/ERPUtils');
          const axios = (await import('axios')).default;

          // Only the admin flow can change roles (the roles picker is disabled
          // for self-edits). Sending `role` from a self-service save corrupts
          // the user's role list because update_user_details destructively
          // replaces all roles with the single value sent.
          const payload: Record<string, any> = {
            email: finalData.email,
            first_name: finalData.first_name,
            last_name: finalData.last_name,
            middle_name: finalData.middle_name || null,
            username: finalData.username,
            timezone: finalData.timezone,
            ...avatarPayload,
            gender: finalData.gender || null,
            birth_date: finalData.birthdate || null,
            interest: finalData.interests || null,
            location: finalData.location || null,
            job_role: finalData.job_role || null,
            phone: finalData.phone || null,
            mobile_no: finalData.mobile_number || null,
            department: finalData.department || null,
            employee_id: finalData.employee_id || null,
            bio: finalData.bio || null,
            name: `${finalData.first_name} ${finalData.last_name}`,
            updated_at: new Date().toISOString(),
          };
          if (!isCurrentUser) {
            payload.role = finalData?.roles?.[0];
          }

          const response = await axios.post(
            getApiEndpoint("EDIT_USER"),
            payload,
            { withCredentials: true }
          );

          if (response.data.message.status === "error") {
            throw new Error(response.data.message.message || "User update failed");
          }

          toast.success("User updated successfully");

          // Call the data refresh callback to fetch updated data FIRST
          if (onDataRefresh) {
            await onDataRefresh();
          }

          // Clear avatar preview state AFTER data refresh completes
          // This prevents showing old avatar between clearing preview and form update
          setAvatarPreviewUrl(null);
          setSelectedAvatarFile(null);
          setAvatarRemoved(false);
        } catch (error) {
          const errorMessage = axios.isAxiosError(error)
            ? (error.response?.data as any)?.message?.message || error.message
            : error instanceof Error ? error.message : "An unknown error occurred";
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      }
    },
    [form, loading, selectedAvatarFile, avatarRemoved, onDataRefresh, isCurrentUser]
  );

  // Watched values for the avatar tile
  const currentAvatar = form.watch('avatar');
  const firstName = form.watch('first_name');
  const lastName = form.watch('last_name');

  const statusLabel = user.is_pending
    ? 'Pending'
    : (user.status || 'active').charAt(0).toUpperCase() + (user.status || 'active').slice(1);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-[230px_1fr] gap-6 items-start">
          {/* Left rail: profile image + account status */}
          <div className="space-y-4">
            <EditUserAvatarSection
              form={form}
              getInitials={getInitials}
              onAvatarFileSelected={handleAvatarFileSelection}
              avatarPreviewUrl={avatarPreviewUrl}
              selectedAvatarFile={selectedAvatarFile}
              currentAvatarUrl={currentAvatar || ''}
              firstName={firstName || ''}
              lastName={lastName || ''}
            />

            <div className="rounded-xl border border-border bg-card/40 p-5">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 flex-shrink-0 text-primary/80" />
                <span className="text-sm font-medium">Account Status: {statusLabel}</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Changes take effect immediately after saving.
              </p>
            </div>
          </div>

          {/* Right column: form sections */}
          <div className="space-y-6">
            <Suspense fallback={<PageLoader type="card-grid" message="Loading user details..." />}>
              <EditUserDetailsSection form={form} isCurrentUser={isCurrentUser} />
            </Suspense>

            <Suspense fallback={<PageLoader type="card-grid" message="Loading additional info..." />}>
              <EditUserAdditionalInfoSection form={form} isCurrentUser={isCurrentUser} />
            </Suspense>

            <Suspense fallback={<PageLoader type="skeleton" message="Loading roles..." />}>
              <EditUserRolesSection
                form={form}
                availableRoles={availableRoles}
                loadingRoles={loadingRoles}
                isCurrentUser={isCurrentUser}
                userRoles={user.roles}
              />
            </Suspense>

            <FormSection icon={Heart} title="Bio & Interests">
              <FormField
                control={form.control}
                name="interests"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Interests</FormLabel>
                    <FormControl>
                      <InterestsInput value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="bio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Bio</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Brief description of expertise and background..."
                        className="min-h-[100px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex justify-end gap-3 border-t border-border pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
});

EditUserForm.displayName = 'EditUserForm';
